import { useSearchParams } from "react-router-dom";
import { PropagationStoreStatus } from "@/utils/campaign-types";

interface UsePropagationFiltersReturn {
  page: number;
  perPage: number;
  statusFilter: PropagationStoreStatus | undefined;
  storeCodeFilter: string | undefined;
  updateFilters: (updates: Record<string, string | null>) => void;
  setPage: (page: number) => void;
  setPerPage: (perPage: number) => void;
  setStatusFilter: (status: PropagationStoreStatus | null) => void;
  setStoreCodeFilter: (storeCode: string | null) => void;
  clearFilters: () => void;
}

/**
 * Hook customizado para gerenciar filtros de propagação via URL params
 * Centraliza toda lógica de filtros e paginação em um único lugar
 */
export function usePropagationFilters(): UsePropagationFiltersReturn {
  const [searchParams, setSearchParams] = useSearchParams();

  // Parse current filters from URL
  const page = parseInt(searchParams.get("page") || "1");
  const perPage = parseInt(searchParams.get("perPage") || "10");
  const statusFilter = searchParams.get("status") as PropagationStoreStatus | undefined;
  const storeCodeFilter = searchParams.get("storeCode") || undefined;

  // Generic update function
  const updateFilters = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "") {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams);
  };

  // Specific setter functions
  const setPage = (newPage: number) => updateFilters({ page: newPage.toString() });
  const setPerPage = (newPerPage: number) => updateFilters({ perPage: newPerPage.toString(), page: "1" });
  const setStatusFilter = (status: PropagationStoreStatus | null) => updateFilters({ status, page: "1" });
  const setStoreCodeFilter = (storeCode: string | null) => updateFilters({ storeCode, page: "1" });

  const clearFilters = () => {
    setSearchParams(new URLSearchParams({ page: "1", perPage: perPage.toString() }));
  };

  return {
    page,
    perPage,
    statusFilter,
    storeCodeFilter,
    updateFilters,
    setPage,
    setPerPage,
    setStatusFilter,
    setStoreCodeFilter,
    clearFilters,
  };
}
